interface Parish {
  id: number;
  name: string;
  slug: string;
  location?: string;
  photo?: string;
  archdeaconry?: {
    id: number;
    name: string;
    slug: string;
  };
  priests?: {
    id: number;
    title: string;
    name: string;
    slug: string;
    photo?: string;
  }[];
}

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { fetchParishBySlug } from '@/services/api';
import { MapPin, Church, Users, ArrowLeft, ShieldCheck, Calendar, Info, ChevronRight } from 'lucide-react';
import ImageHelper from '../lib/imageHelper';
import SeoHead from '@/components/Seo';
import { motion } from 'framer-motion';

// ... (Interface remains the same)

const ParishProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [parish, setParish] = useState<Parish | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadParish = async () => {
      try {
        const response = await fetchParishBySlug(slug!);
        setParish(response.data);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch parish details.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    loadParish();
  }, [slug, toast]);

  // --- SEO BLOCK ---
  const seoContent = (
    <SeoHead
      title={parish ? `${parish.name} | Parishes` : "Loading Parish..."}
      description={parish ? `Visit ${parish.name} located at ${parish.location || 'Ibadan'}. Part of the ${parish.archdeaconry?.name} in the Ibadan South Diocese.` : "View parish details and resident clergy."}
      ogImage={parish?.photo}
    />
  );

  if (loading) return (
    <>
      {seoContent}
      <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
        <div className="flex flex-col items-center">
          <Church className="h-12 w-12 text-amber-600 animate-bounce mb-4" />
          <p className="font-serif italic text-slate-400">Opening Parish Records...</p>
        </div>
      </div>
    </>
  );

  if (!parish) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
      <div className="text-center p-12 bg-white rounded-[2rem] shadow-sm ring-1 ring-slate-100">
        <h2 className="text-3xl font-serif font-bold text-slate-800 mb-4">Parish Not Found</h2>
        <Link to="/parishes" className="px-6 py-2 bg-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-900 transition-all inline-block">
          Return to Directory
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] pb-24">
      {seoContent}

      {/* Top Navigation */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/parishes" className="inline-flex items-center text-slate-400 hover:text-amber-700 transition-colors group text-[10px] font-black uppercase tracking-[0.2em]">
          <ArrowLeft className="h-3 w-3 mr-2 group-hover:-translate-x-1 transition-transform" />
          The Directory
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Left Column: Parish Identity */}
          <div className="lg:col-span-8 space-y-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-[16/9] rounded-[3rem] overflow-hidden shadow-2xl ring-1 ring-slate-200 bg-white"
            >
              <ImageHelper
                src={parish.photo}
                alt={parish.name}
                className="w-full h-full object-cover transition-transform duration-[2s] hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/20 to-transparent" />
              <div className="absolute bottom-10 left-10 right-10">
                <span className="bg-amber-500 text-white text-[9px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-full mb-4 inline-block shadow-lg">
                  Parish Station
                </span>
                <h1 className="text-4xl md:text-6xl font-serif font-bold text-white leading-tight">
                  {parish.name}
                </h1>
              </div>
            </motion.div>

            <div className="bg-white rounded-[2.5rem] p-10 shadow-sm ring-1 ring-slate-100 border-b-4 border-amber-600/20">
              <div className="flex items-center gap-3 mb-8">
                <div className="h-1.5 w-8 bg-amber-600 rounded-full" />
                <h2 className="text-xs font-black uppercase tracking-[0.3em] text-slate-400">Jurisdiction & Location</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="flex items-start gap-5">
                  <div className="bg-amber-50 p-4 rounded-2xl text-amber-600">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">Physical Address</p>
                    <p className="text-slate-700 font-serif text-lg leading-tight">{parish.location || 'Ibadan, Nigeria'}</p>
                  </div>
                </div>

                {parish.archdeaconry && (
                  <div className="flex items-start gap-5">
                    <div className="bg-slate-900 p-4 rounded-2xl text-amber-400 shadow-lg">
                      <Church className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">Assigned To</p>
                      <Link
                        to={`/archdeaconries/${parish.archdeaconry.slug}`}
                        className="text-amber-700 font-serif text-lg font-bold hover:text-slate-900 transition-colors block leading-tight"
                      >
                        {parish.archdeaconry.name}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Added Placeholder for Parish History/Notes */}
            <div className="p-10 rounded-[2.5rem] border-2 border-dashed border-slate-200">
              <div className="flex items-center gap-3 text-slate-400 mb-4">
                <Info className="h-5 w-5" />
                <span className="text-sm font-serif italic">Parish history and weekly activities are currently being digitized by the diocesan secretariat.</span>
              </div>
            </div>
          </div>

          {/* Right Column: Resident Clergy */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-slate-200/50 ring-1 ring-slate-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5">
                <Users className="h-24 w-24" />
              </div>

              <div className="flex items-center justify-between mb-10 relative z-10">
                <h2 className="text-xl font-serif font-bold text-slate-900">
                  Resident Clergy
                </h2>
                <div className="h-8 w-8 rounded-full bg-slate-50 flex items-center justify-center text-[10px] font-black text-slate-400">
                  {parish.priests?.length || 0}
                </div>
              </div>

              {parish.priests && parish.priests.length > 0 ? (
                <div className="space-y-4 relative z-10">
                  {parish.priests.map((clergy) => (
                    <Link
                      key={clergy.id}
                      to={`/clergy/${clergy.slug}`}
                      className="flex items-center gap-4 group bg-slate-50/50 hover:bg-white p-4 rounded-[1.5rem] transition-all ring-1 ring-transparent hover:ring-slate-100 hover:shadow-lg hover:shadow-amber-900/5"
                    >
                      <div className="w-14 h-14 rounded-xl overflow-hidden shadow-sm grayscale group-hover:grayscale-0 transition-all duration-500">
                        <img
                          src={clergy.photo || "https://res.cloudinary.com/dwrqyrm84/image/upload/v1749389564/mq1jwwqt9xxehogbwlem.png"}
                          alt={clergy.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[8px] font-black text-amber-600 uppercase tracking-widest mb-1">
                          {clergy.title}
                        </p>
                        <p className="text-sm font-bold text-slate-900 group-hover:text-amber-800 transition-colors truncate">
                          {clergy.name}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50/50 rounded-[1.5rem] border border-dashed border-slate-200">
                  <p className="text-xs text-slate-400 font-serif italic">No clergy assignments <br />registered at this time.</p>
                </div>
              )}
            </div>

            {/* Quick Actions / Link back */}
            <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white">
              <h4 className="text-amber-400 text-[10px] font-black uppercase tracking-widest mb-4">Diocesan Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/contact" className="text-sm text-slate-300 hover:text-white flex items-center justify-between group">
                    Request Certificate <ChevronRight className="h-4 w-4 text-amber-600 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </li>
                <li>
                  <Link to="/events" className="text-sm text-slate-300 hover:text-white flex items-center justify-between group">
                    Upcoming Programs <ChevronRight className="h-4 w-4 text-amber-600 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ParishProfile;